import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../../../shared/components/AppHeader.js";
import { getPeriodDataset, listAvailablePeriods } from "../lib/periods/index.js";
import { normalizeIrregularBlocks, profileForIrregularBlock } from "../lib/irregular.js";
import { FAIXAS, HORARIOS, faixasVisiveis, layoutDia } from "../lib/landscapeGrid.js";
import {
  dateToISO,
  parseISODate,
  addDays,
  getWeekStart,
  getBlockWeeks,
  createScheduleEngine
} from "../lib/engine.js";
import "../styles/horario.css";

const STORAGE_KEY = "medhelp.profile.v1";
const DAYS = ["", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira"];
const SHORT_DAYS = ["", "Seg", "Ter", "Qua", "Qui", "Sex"];

export default function HorarioPage() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    return mountHorario(root);
  }, []);

  return (
    <>
    <AppHeader title="Horário" />
    <div className="horario-app" ref={rootRef}>
      <div className="app-shell">
        <div className="schedule-toolbar">
          <Link to="/" className="landscape-back" aria-label="Voltar para med41">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M16 10H4m5-5-5 5 5 5" /></svg>
          </Link>
          <button id="blockButton" className="block-chip" type="button" aria-label="Ver outro bloco temático">
            <span id="scheduleContext" className="schedule-context"></span>
            <span className="block-chip-text" aria-hidden="true">
              <span id="blockLabel" className="block-chip-label"></span>
              <span id="blockSyndrome" className="block-chip-syndrome"></span>
            </span>
            <span id="blockNumber" className="block-number" aria-hidden="true"></span>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 8 4 4 4-4" /></svg>
          </button>
          <nav id="weekNavigation" className="week-navigation" aria-label="Navegação por semana">
            <button id="previousWeek" type="button" aria-label="Semana anterior" aria-controls="weekSchedule">
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m12 5-5 5 5 5" /></svg>
            </button>
            <div className="week-navigation-label" aria-live="polite" aria-atomic="true">
              <strong><span id="weekNumber"></span><span id="weekTotal" className="week-total"></span></strong>
              <span id="weekDates"></span>
            </div>
            <button id="nextWeek" type="button" aria-label="Próxima semana" aria-controls="weekSchedule">
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m8 5 5 5-5 5" /></svg>
            </button>
          </nav>
          <button id="profileButton" className="profile-chip" type="button" aria-label="Editar turma e horário">
            <span id="profileInitials">A3</span>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 8 4 4 4-4" /></svg>
          </button>
        </div>
        <main id="mainContent">
          <h1 className="sr-only">Seu horário</h1>
          <div id="weekdayTabs" className="weekday-tabs" role="tablist" aria-label="Dia da semana"></div>
          <div className="schedule-grid">
            <div id="timeAxis" className="time-axis" aria-hidden="true">
              <div className="day-heading">&nbsp;</div>
              <div className="day-events"></div>
            </div>
            <div id="weekSchedule" className="week-schedule"></div>
          </div>
        </main>
      </div>

      <dialog id="settingsDialog" className="sheet-dialog" aria-labelledby="settingsTitle">
        <form id="settingsForm">
          <header className="sheet-header">
            <div>
              <h2 id="settingsTitle">Organize seu horário</h2>
              <p>Sua turma, seus grupos. Tudo em um lugar.</p>
            </div>
            <button id="closeSettings" className="icon-button" type="button" aria-label="Fechar configurações">×</button>
          </header>
          <div className="sheet-body">
            <fieldset className="schedule-mode-field">
              <legend>Como você cursa os blocos?</legend>
              <div className="number-choices choices-two">
                <label className="number-choice"><input type="radio" name="scheduleMode" value="regular" defaultChecked /><span>Regular</span></label>
                <label className="number-choice"><input type="radio" name="scheduleMode" value="irregular" /><span>Irregular</span></label>
              </div>
            </fieldset>
            <fieldset>
              <legend>Período</legend>
              <select id="periodSelect" name="periodo"></select>
            </fieldset>
            <p id="periodMissingNotice" className="field-help" hidden>
              Ainda não temos os dados desse período. Assim que a gente tiver, aparece aqui.
            </p>
            <div id="periodDataFields">
              <fieldset>
                <legend>Selecione sua turma</legend>
                <div id="letterChoices" className="number-choices choices-three"></div>
              </fieldset>
              <fieldset>
                <legend>Selecione seu subgrupo</legend>
                <div id="classGroupChoices" className="number-choices choices-four"></div>
                <p className="field-help">Para GT, PL, PE e TH.</p>
              </fieldset>
              <details id="legacyGroups" className="settings-details" hidden>
                <summary>Grupos de sala por síndrome</summary>
                <div className="details-body">
                  <p className="field-help">Mantivemos os grupos diferentes do seu perfil anterior. Escolher um subgrupo acima aplica o mesmo número a todas.</p>
                  <div id="legacyGroupChoices"></div>
                </div>
              </details>
              <details className="settings-details" id="clinicDetails">
                <summary><span>Ambulatórios<small id="clinicSummary"></small></span><span className="details-plus" aria-hidden="true">+</span></summary>
                <div className="details-body">
                  <p className="field-help">Os grupos de ambulatório são independentes do seu subgrupo de sala.</p>
                  <fieldset><legend>Ambulatório fixo</legend><div id="fixedGroupChoices" className="number-choices"></div></fieldset>
                  <div id="legacyClinicOptions">
                    <fieldset><legend>Especialidade · Reumato-Orto</legend><div id="aeReumatoChoices" className="number-choices"></div></fieldset>
                    <fieldset><legend>Especialidade · Cirurgia</legend><div id="aeCirurgicasChoices" className="number-choices"></div></fieldset>
                    <fieldset><legend>Especialidade · Saúde da Mulher</legend><div id="aeObstetriciaChoices" className="number-choices"></div></fieldset>
                  </div>
                  <div id="otherSpecialtyChoices"></div>
                </div>
              </details>
              <section className="extra-section" aria-labelledby="extrasTitle">
                <h3 id="extrasTitle">Optativas e extensão</h3>
                <div id="extraActivitiesList"></div>
                <button id="addMoreActivity" className="text-button" type="button">+ Adicionar mais</button>
              </section>
              <details className="settings-details" id="rotationDetails">
                <summary><span>Rotação por bloco<small>Confira a sequência da turma</small></span><span className="details-plus" aria-hidden="true">+</span></summary>
                <div className="details-body">
                  <p className="field-help">Sequência padrão da turma. Você pode ajustar cada bloco.</p>
                  <div id="regularRotationFields">
                    <label className="input-field">Bloco 2<select name="rotation2"></select></label>
                    <label className="input-field">Bloco 3<select name="rotation3"></select></label>
                    <label className="input-field">Bloco 4<select name="rotation4"></select></label>
                  </div>
                  <div id="irregularRotationFields" hidden>
                    <p className="field-help">Cada bloco pode usar a grade, a turma e o grupo de outro período. Os ambulatórios deste bloco podem ser ajustados separadamente.</p>
                    <div id="irregularBlockChoices"></div>
                  </div>
                </div>
              </details>
            </div>
            <p id="settingsError" className="form-error" role="alert" hidden></p>
          </div>
          <footer className="sheet-footer"><button id="saveSettings" className="primary-button" type="submit">Ver meu horário</button></footer>
        </form>
      </dialog>

      <dialog id="extraDialog" className="sheet-dialog" aria-labelledby="extraTitle">
        <form id="extraForm">
          <header className="sheet-header">
            <h2 id="extraTitle">Adicionar disciplina</h2>
            <button type="button" className="icon-button" data-close-dialog="extraDialog" aria-label="Fechar disciplina">×</button>
          </header>
          <div className="sheet-body">
            <div className="switch-field">
              <span>Tipo</span>
              <div className="number-choices choices-two">
                <label className="number-choice"><input type="radio" name="kind" value="elective" defaultChecked /><span>Optativa</span></label>
                <label className="number-choice"><input type="radio" name="kind" value="extension" /><span>Extensão</span></label>
              </div>
            </div>
            <label className="input-field">Disciplina<select name="catalogId" required></select></label>
            <div id="manualActivityFields" hidden>
              <label className="input-field">Nome da disciplina<input name="title" maxLength={160} /></label>
              <label className="input-field">Professor<input name="professor" maxLength={100} /></label>
            </div>
            <p id="catalogTeacher" className="field-help"></p>
            <label className="input-field">Dia da semana<select name="weekday"></select></label>
            <div className="input-row">
              <label className="input-field">Início<input type="time" name="start" required /></label>
              <label className="input-field">Término<input type="time" name="end" required /></label>
            </div>
            <details className="settings-details">
              <summary>Período da atividade</summary>
              <div className="details-body">
                <div className="input-row">
                  <label className="input-field">De<input name="startDate" type="date" required /></label>
                  <label className="input-field">Até<input name="endDate" type="date" required /></label>
                </div>
              </div>
            </details>
            <button type="button" id="toggleCustomDay" className="text-button">Personalizar</button>
            <p id="extraError" className="form-error" role="alert" hidden></p>
          </div>
          <footer className="sheet-footer"><button className="primary-button" type="submit">Incluir no horário</button></footer>
        </form>
      </dialog>

      <dialog id="blockDialog" className="sheet-dialog" aria-labelledby="blockTitle">
        <header className="sheet-header">
          <div>
            <h2 id="blockTitle">Ver outro bloco</h2>
            <p>A síndrome de cada bloco segue a rotação do seu perfil.</p>
          </div>
          <button type="button" id="closeBlockDialog" className="icon-button" aria-label="Fechar seleção de bloco">×</button>
        </header>
        <div className="sheet-body">
          <fieldset>
            <legend>Bloco temático</legend>
            <div id="blockChoices" className="number-choices choices-four"></div>
          </fieldset>
          <button type="button" id="resetBlockView" className="text-button">Voltar para hoje</button>
        </div>
      </dialog>

      <div className="toast" id="toast" role="status" aria-live="polite"></div>
    </div>
    </>
  );
}

// Toda a lógica abaixo é o app.js original, com duas mudanças mecânicas: `$`/os querySelectorAll
// ficam presos a `root` (em vez de `document`), e cada listener guarda uma referência pra poder
// ser removido no cleanup devolvido ao useEffect.
function mountHorario(root) {
  const $ = (selector) => root.querySelector(selector);
  const escapeHTML = (value) => String(value ?? "").replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]);
  // crypto.randomUUID só existe em "secure context" (https, ou http://localhost) — acessando
  // pelo IP da rede (ex.: http://192.168.x.x:3000) o navegador não expõe a função. Sem UUID
  // de verdade aqui: o id só precisa ser único o bastante pra comparar atividades na lista.
  const generateId = () =>
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : "id-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  const settingsDialog = $("#settingsDialog");
  const settingsForm = $("#settingsForm");
  const extraDialog = $("#extraDialog");
  const extraForm = $("#extraForm");
  const blockDialog = $("#blockDialog");
  const wideLayout = matchMedia("(min-width:768px), (orientation:landscape) and (min-width:560px)");
  // Celular deitado: a semana vira a grade de horários (lib/landscapeGrid.js), não a régua proporcional.
  const landscapeMedia = matchMedia("(orientation:landscape) and (max-width:950px)");

  $("#periodSelect").innerHTML = Array.from({ length: 12 }, (_, index) => index + 1)
    .map((number) => '<option value="' + number + '">' + number + "º período</option>").join("");

  function now() {
  const override = new URLSearchParams(window.location.search).get("date");
  const parsed = override ? new Date(override) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}
  function referenceDate() {
    if (weekOverride) return parseISODate(weekOverride);
    if (!blockOverride) return now();
    const block = dataset.blocks.find((item) => item.number === blockOverride);
    return block ? parseISODate(block.start) : now();
  }
  // Cada período tem seu próprio conjunto de dados (síndromes, blocos, grade, catálogo de
  // optativas) e por isso sua própria engine (ver createScheduleEngine em lib/engine.js).
  // Um período sem dataset registrado (ainda não extraído dos PDFs) deixa `engine` como
  // null — todo o resto do código checa isso e mostra um aviso em vez de quebrar.
  function setPeriodo(nextPeriodo) {
    periodo = nextPeriodo;
    dataset = getPeriodDataset(periodo);
    engine = dataset ? createScheduleEngine(dataset) : null;
  }
  function emptyProfile() {
    return { schemaVersion: 2, configured: false, periodo, scheduleMode: "regular", irregularBlocks: {}, extraActivities: [] };
  }
  function withScheduleMode(normalized, raw = {}) {
    return { ...normalized, periodo, scheduleMode: raw.scheduleMode === "irregular" ? "irregular" : "regular",
      irregularBlocks: normalizeIrregularBlocks(raw.irregularBlocks, periodo, normalized) };
  }
  function loadProfile() {
    let raw;
    try { raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
    catch { raw = {}; }
    setPeriodo(Number(raw.periodo) || 8);
    // normalizeProfile só devolve os campos que conhece — periodo não é um deles, então
    // tem que ser reanexado depois, ou some do que é salvo no próximo submit.
    return engine ? withScheduleMode(engine.normalizeProfile({ ...raw, periodo }), raw) : emptyProfile();
  }

  let periodo;
  let dataset;
  let engine;
  let profile = loadProfile();
  let draft = engine ? withScheduleMode(engine.normalizeProfile(profile), profile) : emptyProfile();
  let selectedDay = Math.min(5, Math.max(1, now().getDay()));
  let lastDate = dateToISO(now());
  let settingsRequired = false;
  let editingExtraId = null;
  let toastTimer;
  let freshClinicEdits = new Set();
  let customDaySelection = false;
  let blockOverride = null;
  let weekOverride = null;

  // Bloco da semana de `date`, não só do dia: a segunda antes do início de um bloco (ou um
  // feriado entre blocos) já mostra a semana 1 dele, em vez de ficar sem bloco e sem navegação.
  function blockForWeek(date) {
    const byDate = engine.getBlockForDate(date);
    if (byDate) return byDate;
    const monday = dateToISO(getWeekStart(date));
    const friday = dateToISO(addDays(getWeekStart(date), 4));
    return dataset.blocks.find((block) => block.start <= friday && block.end >= monday) || null;
  }

  function renderWeekNavigation(block, reference) {
    $("#weekNavigation").hidden = !block;
    if (!block) return;
    const weeks = getBlockWeeks(block);
    const monday = getWeekStart(reference);
    const index = weeks.findIndex((date) => dateToISO(date) === dateToISO(monday));
    const shortDate = (date) => dateToISO(date).slice(8) + "/" + dateToISO(date).slice(5, 7);
    $("#weekNumber").textContent = "Semana " + (index + 1);
    $("#weekTotal").textContent = " de " + weeks.length;
    $("#weekNumber").title = (index + 1) + " de " + weeks.length + " semanas do bloco";
    $("#weekDates").textContent = shortDate(monday) + "–" + shortDate(addDays(monday, 4));
    $("#previousWeek").disabled = index <= 0;
    $("#nextWeek").disabled = index >= weeks.length - 1;
  }

  function moveWeek(offset) {
    if (!engine) return;
    const reference = referenceDate();
    const block = dataset.blocks.find((item) => item.number === blockOverride) || blockForWeek(reference);
    if (!block) return;
    const weeks = getBlockWeeks(block);
    const index = weeks.findIndex((date) => dateToISO(date) === dateToISO(getWeekStart(reference)));
    if (index < 0 || !weeks[index + offset]) return;
    blockOverride = block.number;
    weekOverride = dateToISO(weeks[index + offset]);
    renderSchedule();
  }

  function showToast(message) {
    clearTimeout(toastTimer);
    $("#toast").textContent = message;
    $("#toast").classList.add("is-visible");
    toastTimer = setTimeout(() => $("#toast").classList.remove("is-visible"), 2400);
  }
  function showError(id, message = "") {
    $(id).textContent = message;
    $(id).hidden = !message;
  }
  const WEEK_RANGE_DEFAULT = { start: 450, end: 1150 }; // 07:30–19:10, alarga se algum evento passar disso
  function weekTimeRange(week) {
    let start = WEEK_RANGE_DEFAULT.start;
    let end = WEEK_RANGE_DEFAULT.end;
    for (const day of week) for (const event of day.events) {
      if (!Number.isFinite(event.startMinutes)) continue;
      start = Math.min(start, event.startMinutes);
      end = Math.max(end, event.endMinutes ?? event.startMinutes);
    }
    return { start, end, span: Math.max(1, end - start) };
  }
  // Marca cada evento com a "pista" (lane) dele entre os que se sobrepõem, pra não desenhar um em cima do outro.
  function withLanes(events) {
    const timed = events.filter((event) => Number.isFinite(event.startMinutes));
    return events.map((event) => {
      if (!Number.isFinite(event.startMinutes)) return event;
      const group = timed.filter((other) => other === event ||
        (event.overlaps || []).includes(other.title) || (other.overlaps || []).includes(event.title));
      return { ...event, laneIndex: group.indexOf(event), laneCount: group.length };
    });
  }
  // Régua de horários (só visível em telas largas/landscape via CSS) — marca o início e o
  // fim de cada bloco de aula (não hora cheia), na mesma escala --pos usada pelas aulas.
  const AXIS_SLOTS = [
    [450, 550],   // 07:30–09:10
    [570, 670],   // 09:30–11:10
    [690, 740],   // 11:30–12:20
    [810, 910],   // 13:30–15:10
    [930, 1030],  // 15:30–17:10
    [1050, 1150]  // 17:30–19:10
  ];
  function hourMarks() {
    const minutes = new Set();
    for (const [start, end] of AXIS_SLOTS) { minutes.add(start); minutes.add(end); }
    return [...minutes].sort((a, b) => a - b);
  }
  function minutesLabel(total) {
    const hours = String(Math.floor(total / 60)).padStart(2, "0");
    const mins = String(total % 60).padStart(2, "0");
    return hours + ":" + mins;
  }
  function renderHourLine(minutes, range) {
    const pos = ((minutes - range.start) / range.span * 100).toFixed(3);
    return '<div class="hour-mark" style="--pos:' + pos + '"></div>';
  }
  function renderHourLabel(minutes, range) {
    const pos = ((minutes - range.start) / range.span * 100).toFixed(3);
    return '<div class="time-axis-label" style="--pos:' + pos + '">' + minutesLabel(minutes) + "</div>";
  }
  function renderLesson(event, date, reference, range) {
    if (event.kind === "seminar" && Number.isFinite(event.startMinutes)) {
      const pos = ((event.startMinutes - range.start) / range.span * 100).toFixed(3);
      return '<article class="seminar-marker" style="--pos:' + pos + '">' +
        '<div class="seminar-marker-time"><strong>' + escapeHTML(event.start) + '</strong>' +
        (event.timeLabel !== event.start ? '<span>' + escapeHTML(event.after ? 'após ' + event.after : '') + '</span>' : '') + '</div>' +
        '<div class="seminar-marker-body"><small>Seminário</small><h3>' + escapeHTML(event.title) + '</h3>' +
        '<p>' + escapeHTML(event.professor) + (event.inferred ? ' · horário inferido' : '') + '</p></div></article>';
    }
    if (!Number.isFinite(event.startMinutes)) return '<p class="calendar-note">' + escapeHTML(event.title) + "</p>";
    const minutes = reference.getHours() * 60 + reference.getMinutes();
    // Sem "agora"/"hoje" numa pré-visualização de outro bloco — não é a hora real.
    const isCurrent = dateToISO(date) === dateToISO(reference) && minutes >= event.startMinutes && minutes < event.endMinutes;
    const candidates = event.kind === "conflict" ? event.candidates || [] : [];
    const title = candidates.length ? candidates.map((item) => item.title).join(" / ") : event.title;
    const professor = candidates.length ? [...new Set(candidates.map((item) => item.professor).filter(Boolean))].join(" / ") : event.professor;
    const gtLocation = event.kind === "gt" && event.location
      ? (/^\d/.test(event.location) ? "Sala " : "") + event.location : "";
    const professorLine = gtLocation ? gtLocation + (professor ? " · " + professor : "") : professor;
    const hasConflict = event.kind === "conflict" || event.overlaps?.length;
    const pos = ((event.startMinutes - range.start) / range.span * 100).toFixed(3);
    const size = (((event.endMinutes ?? event.startMinutes) - event.startMinutes) / range.span * 100).toFixed(3);
    const laneCount = event.laneCount || 1;
    const lanePos = ((event.laneIndex || 0) / laneCount * 100).toFixed(3);
    const laneSize = (100 / laneCount).toFixed(3);
    const style = 'style="--pos:' + pos + ';--size:' + size + ';--lane-pos:' + lanePos + ';--lane-size:' + laneSize + '"';
    const heading = event.kind === "clinic"
      ? '<h3>Ambulatório</h3><p class="lesson-specialty">' + escapeHTML(title) + '</p>'
      : '<h3>' + escapeHTML(title) + '</h3>';
    return '<article class="lesson lesson--' + event.kind + (event.exam ? " is-exam" : "") + (isCurrent ? " is-current" : "") + '" ' + style + '>' +
      '<div class="lesson-time"><strong>' + escapeHTML(event.start) + '</strong><span>' + escapeHTML(event.end) + "</span></div>" +
      '<div class="lesson-body"><span class="lesson-code">' + escapeHTML(event.code) + '</span>' + heading +
      (professorLine ? '<p class="lesson-professor">' + escapeHTML(professorLine) + "</p>" : "") +
      (event.exam ? '<p class="lesson-exam">' + escapeHTML(event.examDetail) + "</p>" : "") +
      (hasConflict ? '<p class="lesson-note">Horários sobrepostos. Confira suas escolhas.</p>' : "") +
      (isCurrent ? '<span class="now-label">Agora</span>' : "") + "</div></article>";
  }
  // Card de uma aula na grade de horários do celular deitado. Extras (optativa, ATEX,
  // seminário) ficam nas faixas com borda tracejada; aula de dois horários mostra a sala
  // ou a especialidade na 2ª linha.
  function renderLandscapeCard(item, date) {
    const event = item.event;
    const reference = now();
    const minutes = reference.getHours() * 60 + reference.getMinutes();
    const isCurrent = dateToISO(date) === dateToISO(reference) && minutes >= event.startMinutes && minutes < event.endMinutes;
    const candidates = event.kind === "conflict" ? event.candidates || [] : [];
    // "· Reumato" já aparece no topo do horário, então sai do título do GT.
    const rawTitle = candidates.length ? candidates.map((candidate) => candidate.title).join(" / ") : event.title;
    const title = event.kind === "gt" ? rawTitle.replace(/\s·\s.*$/, "") : rawTitle;
    const professor = candidates.length ? [...new Set(candidates.map((candidate) => candidate.professor).filter(Boolean))].join(" / ") : event.professor;
    const style = "grid-row:" + item.row + " / span " + item.span + ";grid-column:" + (item.lane + 1) + " / " +
      (item.lane === item.lanesDoGrupo - 1 ? item.lanesDoGrupo + 1 : item.lane + 2);
    const kindClass = " lesson--" + event.kind + (event.exam ? " is-exam" : "") + (isCurrent ? " is-current" : "");
    if (item.diaTodo) {
      return '<article class="lesson lg-card lg-card--day' + kindClass + '" style="' + style + '">' +
        '<h3>' + escapeHTML(title) + '</h3>' +
        (professor ? '<p class="lg-second">' + escapeHTML(professor) + "</p>" : "") + "</article>";
    }
    if (item.faixa) {
      const when = item.mostrarHorario && event.start ? event.start : "";
      return '<article class="lesson lg-card lg-card--extra' + kindClass + '" style="' + style + '">' +
        '<h3>' + escapeHTML(title) + '</h3>' +
        ((when || professor) ? '<p>' + escapeHTML([when, professor].filter(Boolean).join(" · ")) + "</p>" : "") + "</article>";
    }
    const isClinic = event.kind === "clinic";
    const location = event.kind === "gt" && event.location ? (/^\d/.test(event.location) ? "Sala " : "") + event.location : "";
    const secondLine = event.exam ? event.examDetail
      : isClinic ? title : location || (item.mostrarHorario && event.start ? event.start + "–" + event.end : "");
    const progress = isCurrent ? ";--progress:" + (((minutes - event.startMinutes) / (event.endMinutes - event.startMinutes)) * 100).toFixed(1) + "%" : "";
    const code = isClinic ? "" : event.code;
    const meta = [code ? '<span class="lesson-code">' + escapeHTML(code) + "</span>" : "", professor ? escapeHTML((code ? " · " : "") + professor) : ""].join("");
    return '<article class="lesson lg-card' + (item.span > 1 ? " lg-card--tall" : "") + kindClass + '" style="' + style + progress + '">' +
      '<div class="lg-head"><h3>' + escapeHTML(isClinic ? "Ambulatório" : title) + '</h3>' +
      (isCurrent ? '<span class="now-label">Agora</span>' : "") + "</div>" +
      (secondLine ? '<p class="lg-second">' + escapeHTML(secondLine) + "</p>" : "") +
      (meta ? '<p class="lesson-professor lg-meta">' + meta + "</p>" : "") +
      (isCurrent ? '<span class="lg-progress" aria-hidden="true"></span>' : "") + "</article>";
  }
  // Grade única (linhas × dias) pro celular deitado: a coluna da esquerda tem os horários e
  // cada dia é uma coluna com subgrid nas mesmas linhas. O posicionamento está em lib/landscapeGrid.js.
  function renderLandscapeWeek(week) {
    const today = dateToISO(now());
    const minutes = now().getHours() * 60 + now().getMinutes();
    const todayInWeek = week.some((day) => dateToISO(day.date) === today);
    const layouts = week.map((day) => layoutDia(day.events));
    const faixas = faixasVisiveis(layouts);
    const isNowSlot = (slot) => todayInWeek && minutes >= slot.inicio && minutes < slot.fim;
    const timeLabels = HORARIOS.map((slot) => '<div class="lg-time' + (isNowSlot(slot) ? " is-now" : "") + '" style="grid-row:' + slot.row + '">' +
      "<strong>" + minutesLabel(slot.inicio) + "</strong><span>" + minutesLabel(slot.fim) + "</span></div>").join("");
    const bands = [];
    const faixaLabels = [];
    for (const [row, visible] of [[4, faixas.almoco], [7, faixas.noite]]) {
      if (!visible) continue;
      const { inicio, fim } = FAIXAS[row];
      bands.push('<div class="lg-band" style="grid-row:' + row + ';grid-column:2 / -1" aria-hidden="true"></div>');
      faixaLabels.push('<div class="lg-time lg-time--faixa" style="grid-row:' + row + '">' +
        "<span>" + minutesLabel(inicio) + "</span><span>" + minutesLabel(fim) + "</span></div>");
    }
    const days = week.map((day, index) => {
      const number = index + 1;
      const layout = layouts[index];
      const isToday = dateToISO(day.date) === today;
      const free = day.events.length || !day.dataWarning
        ? layout.livres.map((row) => '<div class="lg-free" style="grid-row:' + row + ';grid-column:1 / -1" aria-hidden="true"></div>').join("")
        : '<p class="lg-note" style="grid-row:2 / 7">' + escapeHTML(day.dataWarning) + "</p>";
      const cards = layout.items.map((item) => renderLandscapeCard(item, day.date)).join("");
      return '<section id="weekday-' + number + '" class="lg-day' + (isToday ? " is-calendar-today" : "") +
        '" role="region" aria-labelledby="day-heading-' + number + '" style="grid-row:1 / -1;grid-column:' + (number + 1) +
        ';grid-template-columns:repeat(' + layout.lanes + ', minmax(0, 1fr))">' +
        '<h2 class="day-heading lg-heading" id="day-heading-' + number + '" style="grid-row:1;grid-column:1 / -1">' +
        '<span class="lg-weekday">' + SHORT_DAYS[number] + '</span><span class="lg-date">' + day.date.getDate() + "</span></h2>" +
        free + cards + "</section>";
    }).join("");
    $("#weekSchedule").innerHTML = '<div class="landscape-grid" style="--faixa-almoco:' + (faixas.almoco ? 40 : 4) + "px;--faixa-noite:" +
      (faixas.noite ? 40 : 0) + 'px">' + timeLabels + bands.join("") + faixaLabels.join("") + days + "</div>";
  }
  function renderSchedule() {
    if (!engine) {
      $("#profileInitials").textContent = "–";
      $("#scheduleContext").textContent = "Sem dados ainda";
      $("#blockButton").hidden = true;
      $("#weekNavigation").hidden = true;
      $("#weekdayTabs").innerHTML = "";
      $("#timeAxis .day-events").innerHTML = "";
      $("#weekSchedule").innerHTML = '<div class="empty-day" style="grid-column:1/-1">' +
        '<strong>Ainda não temos os dados do ' + periodo + 'º período</strong>' +
        '<p>Assim que a gente tiver, aparece aqui.</p></div>';
      return;
    }
    $("#blockButton").hidden = false;
    const reference = referenceDate();
    const block = dataset.blocks.find((item) => item.number === blockOverride) || blockForWeek(reference);
    renderWeekNavigation(block, reference);
    const assignment = profile.scheduleMode === "irregular" && block ? profile.irregularBlocks?.[block.number] : null;
    const irregular = assignment ? profileForIrregularBlock(assignment, block.number, profile.extraActivities) : null;
    const displayDataset = irregular?.dataset || dataset;
    const displayEngine = irregular ? createScheduleEngine(displayDataset) : engine;
    const displayProfile = irregular?.profile || profile;
    const week = displayEngine.getWeekSchedule(reference, displayProfile);
    const range = weekTimeRange(week);
    const summary = displayEngine.getProfileSummary(displayProfile, block?.number);
    $("#profileInitials").textContent = summary.label;
    $("#scheduleContext").textContent = block
      ? (assignment ? assignment.period + "º período · " : "") + block.label + (summary.syndrome ? " · " + displayDataset.syndromes[summary.syndrome].short : "")
      : "Horário da semana";
    $("#blockButton").title = $("#scheduleContext").textContent;
    $("#blockLabel").textContent = block ? (assignment ? assignment.period + "º período · " : "") + block.label : "";
    $("#blockSyndrome").textContent = summary.syndrome ? displayDataset.syndromes[summary.syndrome].short : block ? "Sem tema" : "Horário da semana";
    $("#blockNumber").textContent = block ? block.number + "º" : "–";
    $("#weekdayTabs").innerHTML = DAYS.slice(1).map((day, index) => {
      const number = index + 1;
      return '<button class="weekday-tab" type="button" role="tab" id="tab-' + number + '" data-day="' + number +
        '" aria-label="' + day + '" aria-controls="weekday-' + number + '" aria-selected="' + (number === selectedDay) +
        '" tabindex="' + (number === selectedDay ? "0" : "-1") + '">' + SHORT_DAYS[number] + "</button>";
    }).join("");
    if (landscapeMedia.matches) {
      renderLandscapeWeek(week);
      $("#timeAxis .day-events").innerHTML = "";
      return;
    }
    const marks = hourMarks();
    const hourLines = marks.map((minutes) => renderHourLine(minutes, range)).join("");
    $("#weekSchedule").innerHTML = week.map((day, index) => {
      const number = index + 1;
      const isToday = dateToISO(day.date) === dateToISO(now());
      const label = wideLayout.matches ? "day-heading-" + number : "tab-" + number;
      const events = withLanes(day.events);
      return '<section id="weekday-' + number + '" class="day-panel' + (number === selectedDay ? " is-selected" : "") +
        (isToday ? " is-calendar-today" : "") + '" role="' + (wideLayout.matches ? "region" : "tabpanel") +
        '" aria-labelledby="' + label + '"><h2 class="day-heading" id="day-heading-' + number + '">' +
        '<span class="day-heading-full">' + DAYS[number] + '</span><span class="day-heading-short">' + SHORT_DAYS[number] + '</span></h2>' +
        '<div class="day-events">' + hourLines + (events.length ? events.map((event) => renderLesson(event, day.date, now(), range)).join("") :
        '<p class="empty-day"><strong>Sem aulas</strong>' + escapeHTML(day.dataWarning || "Seu dia está livre.") + "</p>") + "</div></section>";
    }).join("");
    $("#timeAxis .day-events").innerHTML = marks.map((minutes) => renderHourLabel(minutes, range)).join("");
  }
  function selectDay(day, focus = false) {
    selectedDay = day;
    // Troca de painel sem recriar a aba focada nem mexer na posição de rolagem.
    root.querySelectorAll(".weekday-tab").forEach((button) => {
      const selected = Number(button.dataset.day) === day;
      button.setAttribute("aria-selected", String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    root.querySelectorAll(".day-panel").forEach((panel, index) => panel.classList.toggle("is-selected", index + 1 === day));
    if (focus) $("#tab-" + day).focus();
  }
  function numberChoices(container, name, values, selected) {
    $(container).innerHTML = values.map((value) => '<label class="number-choice"><input type="radio" name="' + name +
      '" value="' + value + '"' + (String(value) === String(selected) ? " checked" : "") + ' /><span>' + value + "</span></label>").join("");
  }
  function range(maximum) { return Array.from({ length: maximum }, (_, index) => index + 1); }
  function openBlockDialog() {
    if (!engine) return;
    const current = blockOverride || blockForWeek(now())?.number || dataset.blocks[0].number;
    numberChoices("#blockChoices", "block", dataset.blocks.map((item) => item.number), current);
    blockDialog.showModal();
  }
  function updateClinicSummary() {
    const clinics = draft.clinics;
    $("#clinicSummary").textContent = periodo === 8
      ? "AF " + clinics.fixedGroup + " · Reumato " + clinics.aeReumato + " · Cirurgia " + clinics.aeCirurgicas + " · Mulher " + clinics.aeObstetricia
      : "AF " + clinics.fixedGroup + Object.entries(clinics.specialtyGroups || {}).filter(([key]) => dataset.specialtyClinics[key]).map(([key, group]) => " · " + dataset.syndromes[key].short + " AE " + group).join("");
  }
  function renderGroupChoices() {
    numberChoices("#letterChoices", "letter", ["A", "B", "C"], draft.letter);
    numberChoices("#classGroupChoices", "sameGroup", range(4), draft.sameGroup);
    numberChoices("#fixedGroupChoices", "fixedGroup", range(draft.letter === "A" ? 6 : 5), draft.clinics.fixedGroup);
    numberChoices("#aeReumatoChoices", "aeReumato", range(5), draft.clinics.aeReumato);
    numberChoices("#aeCirurgicasChoices", "aeCirurgicas", range(5), draft.clinics.aeCirurgicas);
    numberChoices("#aeObstetriciaChoices", "aeObstetricia", range(9), draft.clinics.aeObstetricia);
    const hasClinics = Boolean(Object.keys(dataset.fixedClinics).length || Object.keys(dataset.specialtyClinics).length);
    $("#clinicDetails").hidden = !hasClinics;
    $("#legacyClinicOptions").hidden = periodo !== 8;
    $("#otherSpecialtyChoices").innerHTML = periodo === 8 ? "" : Object.keys(dataset.specialtyClinics).map((key) =>
      '<fieldset><legend>Especialidade · ' + escapeHTML(dataset.syndromes[key].short) + '</legend><div id="specialty-' + key + '" class="number-choices"></div></fieldset>').join("");
    if (periodo !== 8) for (const key of Object.keys(dataset.specialtyClinics)) {
      const max = Math.max(...Object.keys(dataset.specialtyClinics[key]).map(Number));
      numberChoices("#specialty-" + key, "specialty-" + key, range(max), draft.clinics.specialtyGroups[key]);
    }
    const legacyGroups = Object.values(draft.groups).some((value) => value !== draft.sameGroup);
    $("#legacyGroups").hidden = !legacyGroups;
    $("#legacyGroupChoices").innerHTML = legacyGroups ? Object.entries(dataset.syndromes).map(([key, syndrome]) =>
      '<fieldset><legend>' + syndrome.short + '</legend><div id="group-' + key + '" class="number-choices choices-four"></div></fieldset>').join("") : "";
    if (legacyGroups) for (const key of Object.keys(dataset.syndromes)) numberChoices("#group-" + key, "group-" + key, range(4), draft.groups[key]);
    updateClinicSummary();
  }
  function renderExtras() {
    const rows = [];
    for (const [kind, label] of [["elective", "Optativa"], ["extension", "Extensão"]]) {
      const activities = draft.extraActivities.filter((item) => item.kind === kind);
      if (!activities.length) {
        rows.push('<div class="extra-slot"><div><small>' + label + '</small><strong>Nenhuma selecionada</strong></div><button type="button" class="small-button" data-add-kind="' + kind + '">Selecionar ' + label.toLowerCase() + "</button></div>");
      }
      for (const activity of activities) {
        rows.push('<div class="extra-slot"><div><small>' + label + '</small><strong>' + escapeHTML(activity.title) +
          '</strong><p>' + SHORT_DAYS[activity.weekday] + " · " + activity.start + "–" + activity.end +
          '</p></div><div class="extra-actions"><button type="button" class="small-button" data-edit-extra="' + escapeHTML(activity.id) +
          '" aria-label="Editar ' + escapeHTML(activity.title) + '">Editar</button><button type="button" class="small-button remove-extra" data-remove-extra="' +
          escapeHTML(activity.id) + '" aria-label="Remover ' + escapeHTML(activity.title) + '">×</button></div></div>');
      }
    }
    $("#extraActivitiesList").innerHTML = rows.join("");
  }
  function selectOptions(values, selected, labelFor = (value) => value) {
    return values.map((value) => '<option value="' + escapeHTML(value) + '"' + (String(value) === String(selected) ? ' selected' : '') + '>' + escapeHTML(labelFor(value)) + '</option>').join('');
  }
  function renderIrregularBlocks() {
    $("#irregularBlockChoices").innerHTML = [1, 2, 3, 4].map((number) => {
      const item = draft.irregularBlocks[number];
      const blockDataset = getPeriodDataset(item.period);
      const fixedGroups = Object.keys(blockDataset.fixedClinics?.[number]?.[item.letter] || {}).map(Number);
      const specialtyGroups = Object.keys(blockDataset.specialtyClinics?.[item.syndrome] || {}).map(Number);
      return '<section class="irregular-block"><h4>Bloco ' + number + '</h4>' +
        '<div class="input-row"><label class="input-field">Período<select name="irregularPeriod' + number + '">' +
        selectOptions(listAvailablePeriods(), item.period, (value) => value + 'º período') + '</select></label>' +
        '<label class="input-field">Tema<select name="irregularSyndrome' + number + '">' +
        selectOptions(Object.keys(blockDataset.syndromes), item.syndrome, (value) => blockDataset.syndromes[value].name) + '</select></label></div>' +
        '<div class="input-row"><label class="input-field">Turma<select name="irregularLetter' + number + '">' +
        selectOptions(['A', 'B', 'C'], item.letter) + '</select></label>' +
        '<label class="input-field">Subgrupo<select name="irregularGroup' + number + '">' +
        selectOptions([1, 2, 3, 4], item.group) + '</select></label></div>' +
        (fixedGroups.length || specialtyGroups.length ? '<div class="input-row">' +
          (fixedGroups.length ? '<label class="input-field">Ambulatório fixo<select name="irregularFixed' + number + '">' + selectOptions(fixedGroups, item.fixedGroup) + '</select></label>' : '') +
          (specialtyGroups.length ? '<label class="input-field">Ambulatório de especialidade<select name="irregularSpecialty' + number + '">' + selectOptions(specialtyGroups, item.specialtyGroup) + '</select></label>' : '') +
          '</div>' : '') + '</section>';
    }).join('');
  }
  // Reflete no formulário se o período atualmente selecionado (em `draft`, pode ainda não
  // ter sido salvo) tem dados. Sem dados: some com turma/subgrupo/ambulatório/optativas/
  // rotação (nada disso faz sentido sem uma grade real) e mostra só o aviso.
  function renderSettingsFields() {
    const hasData = Boolean(engine);
    settingsForm.querySelectorAll('input[name="scheduleMode"]').forEach((input) => {
      input.checked = input.value === (draft.scheduleMode || "regular");
    });
    $("#periodDataFields").hidden = !hasData;
    $("#periodMissingNotice").hidden = hasData;
    if (!hasData) return;
    renderGroupChoices();
    for (const number of [2, 3, 4]) {
      const select = settingsForm.elements["rotation" + number];
      select.innerHTML = '<option value="">Sem tema definido</option>' + Object.entries(dataset.syndromes).map(([id, value]) => '<option value="' + id + '">' + value.name + "</option>").join("");
      select.value = draft.rotation[number] || "";
    }
    const irregular = draft.scheduleMode === "irregular";
    $("#regularRotationFields").hidden = irregular;
    $("#irregularRotationFields").hidden = !irregular;
    if (irregular) { renderIrregularBlocks(); $("#rotationDetails").open = true; }
    renderExtras();
  }
  function openSettings(required = false) {
    settingsRequired = required;
    draft = engine ? withScheduleMode(engine.normalizeProfile(profile), profile) : emptyProfile();
    freshClinicEdits = new Set();
    $("#periodSelect").value = String(periodo);
    renderSettingsFields();
    $("#closeSettings").hidden = required;
    $("#saveSettings").textContent = required ? "Ver meu horário" : "Salvar alterações";
    showError("#settingsError");
    settingsDialog.showModal();
  }
  function catalogVariants(kind, code) {
    return dataset.extraCatalog.filter((item) => item.kind === kind && item.code === code);
  }
  function catalogVariantForWeekday(kind, code, weekday) {
    return catalogVariants(kind, code).find((item) => item.weekday === Number(weekday)) || null;
  }
  function populateCatalog(kind, value = "") {
    const seen = new Set();
    const courses = dataset.extraCatalog.filter((item) => item.kind === kind && !seen.has(item.code) && seen.add(item.code)).sort((a, b) =>
      Number(b.reference.includes("8º")) - Number(a.reference.includes("8º")) || a.title.localeCompare(b.title, "pt-BR"));
    extraForm.elements.catalogId.innerHTML = '<option value="">Selecione a disciplina</option>' + courses.map((item) =>
      '<option value="' + item.code + '">' + escapeHTML(item.title) + "</option>").join("") +
      '<option value="manual">Outra disciplina · cadastrar</option>';
    extraForm.elements.catalogId.value = value;
  }
  function populateWeekdayOptions(variants) {
    const relevantDays = [...new Set(variants.map((item) => item.weekday))].sort((a, b) => a - b);
    const days = customDaySelection || !relevantDays.length ? [1, 2, 3, 4, 5] : relevantDays;
    const select = extraForm.elements.weekday;
    const previous = select.value;
    select.innerHTML = days.map((day) => '<option value="' + day + '">' + DAYS[day] + "</option>").join("");
    select.value = days.includes(Number(previous)) ? previous : String(days[0]);
  }
  function updateCatalogFields(fill = true) {
    const code = extraForm.elements.catalogId.value;
    const manual = code === "manual";
    const variants = manual || !code ? [] : catalogVariants(extraForm.elements.kind.value, code);
    $("#manualActivityFields").hidden = !manual;
    extraForm.elements.title.required = manual;
    populateWeekdayOptions(variants);
    const variant = variants.length ? catalogVariantForWeekday(extraForm.elements.kind.value, code, extraForm.elements.weekday.value) : null;
    $("#catalogTeacher").textContent = variant?.professor || "";
    if (fill) {
      extraForm.elements.title.value = variant?.title || "";
      extraForm.elements.professor.value = variant?.professor || "";
      extraForm.elements.start.value = variant?.start || "";
      extraForm.elements.end.value = variant?.end || "";
    }
  }
  function openExtra(kind = "elective", id = null) {
    editingExtraId = id;
    extraForm.reset();
    showError("#extraError");
    const activity = id ? draft.extraActivities.find((item) => item.id === id) : null;
    extraForm.elements.kind.value = activity?.kind || kind;
    const activityCode = activity ? (dataset.extraCatalog.find((item) => item.id === activity.catalogId)?.code || "manual") : "";
    customDaySelection = Boolean(activity) && activityCode !== "manual" &&
      !catalogVariants(extraForm.elements.kind.value, activityCode).some((item) => item.weekday === activity.weekday);
    populateCatalog(extraForm.elements.kind.value, activityCode);
    extraForm.elements.startDate.value = activity?.startDate || "2026-07-28";
    extraForm.elements.endDate.value = activity?.endDate || "2026-12-18";
    updateCatalogFields();
    if (activity) for (const field of ["title", "professor", "start", "end", "weekday"]) extraForm.elements[field].value = activity[field];
    $("#extraTitle").textContent = activity ? "Editar disciplina" : "Adicionar disciplina";
    extraDialog.showModal();
  }

  const onProfileButtonClick = () => openSettings(false);
  const onCloseSettingsClick = () => settingsDialog.close();
  const onBlockButtonClick = () => openBlockDialog();
  const onCloseBlockDialogClick = () => blockDialog.close();
  const onBlockChoicesChange = (event) => {
    if (event.target.name !== "block" || !engine) return;
    const today = blockForWeek(now())?.number || null;
    const chosen = Number(event.target.value);
    blockOverride = chosen === today ? null : chosen;
    weekOverride = null;
    blockDialog.close();
    renderSchedule();
  };
  const onResetBlockViewClick = () => {
    blockOverride = null;
    weekOverride = null;
    blockDialog.close();
    renderSchedule();
  };
  const onSettingsCancel = (event) => { if (settingsRequired) event.preventDefault(); };
  const onSettingsChange = (event) => {
    const { name, value } = event.target;
    if (name === "scheduleMode") {
      draft.scheduleMode = value;
      renderSettingsFields();
    } else if (/^irregular(Period|Syndrome|Letter|Group|Fixed|Specialty)[1-4]$/.test(name)) {
      const number = Number(name.slice(-1));
      const field = name.slice(9, -1);
      const item = draft.irregularBlocks[number];
      if (field === "Period" || field === "Letter") {
        item[field === "Period" ? "period" : "letter"] = field === "Period" ? Number(value) : value;
        const selected = getPeriodDataset(item.period);
        item.syndrome = selected.defaultRotations[item.letter]?.[number] || Object.keys(selected.syndromes)[0];
      } else {
        const key = { Syndrome: "syndrome", Group: "group", Fixed: "fixedGroup", Specialty: "specialtyGroup" }[field];
        item[key] = field === "Syndrome" ? value : Number(value);
      }
      draft.irregularBlocks = normalizeIrregularBlocks(draft.irregularBlocks, periodo, draft);
      renderIrregularBlocks();
    } else if (name === "periodo") {
      const scheduleMode = draft.scheduleMode;
      setPeriodo(Number(value));
      draft = engine ? withScheduleMode(engine.createDefaultProfile(), { scheduleMode }) : emptyProfile();
      renderSettingsFields();
    } else if (name === "letter") {
      const previousLetter = draft.letter;
      draft.letter = value;
      draft.clinics.fixedGroup = Math.min(draft.clinics.fixedGroup, value === "A" ? 6 : 5);
      draft.rotation = { ...dataset.defaultRotations[value] };
      for (const item of Object.values(draft.irregularBlocks || {})) if (item.period === periodo && item.letter === previousLetter) item.letter = value;
      draft.irregularBlocks = normalizeIrregularBlocks(draft.irregularBlocks, periodo, draft);
      for (const number of [2, 3, 4]) settingsForm.elements["rotation" + number].value = draft.rotation[number] || "";
      renderGroupChoices();
      if (draft.scheduleMode === "irregular") renderIrregularBlocks();
      settingsForm.querySelector('input[name="letter"]:checked')?.focus();
    } else if (name === "sameGroup") {
      const previousGroup = draft.sameGroup;
      draft.sameGroup = Number(value);
      draft.groups = Object.fromEntries(Object.keys(dataset.syndromes).map((key) => [key, Number(value)]));
      draft.clinics.specialtyGroups = Object.fromEntries(Object.keys(dataset.syndromes).map((key) => [key, Number(value)]));
      if (!profile.configured) for (const key of ["fixedGroup", "aeReumato", "aeCirurgicas", "aeObstetricia"]) {
        if (!freshClinicEdits.has(key)) draft.clinics[key] = Number(value);
      }
      for (const item of Object.values(draft.irregularBlocks || {})) if (item.period === periodo && item.group === previousGroup) item.group = Number(value);
      draft.irregularBlocks = normalizeIrregularBlocks(draft.irregularBlocks, periodo, draft);
      renderGroupChoices();
      if (draft.scheduleMode === "irregular") renderIrregularBlocks();
      settingsForm.querySelector('input[name="sameGroup"]:checked')?.focus();
    } else if (name in draft.clinics) {
      draft.clinics[name] = Number(value);
      freshClinicEdits.add(name);
      updateClinicSummary();
    } else if (name.startsWith("specialty-")) {
      draft.clinics.specialtyGroups[name.slice(10)] = Number(value);
      updateClinicSummary();
    } else if (name.startsWith("group-")) {
      draft.groups[name.slice(6)] = Number(value);
    }
  };
  const onSettingsSubmit = (event) => {
    event.preventDefault();
    let next;
    if (engine) {
      for (const number of [2, 3, 4]) draft.rotation[number] = settingsForm.elements["rotation" + number].value;
      const normalized = engine.normalizeProfile({ ...draft, periodo, configured: true });
      next = withScheduleMode(normalized, { ...draft, irregularBlocks: draft.irregularBlocks });
    } else {
      next = emptyProfile();
    }
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); }
    catch { showError("#settingsError", "Não foi possível salvar neste navegador. Confira o espaço disponível e tente novamente."); return; }
    if (profile.periodo !== periodo) { blockOverride = null; weekOverride = null; }
    profile = next;
    settingsRequired = false;
    settingsDialog.close();
    renderSchedule();
    showToast(engine ? "Horário salvo neste aparelho." : "Período salvo. Avisamos quando os dados chegarem.");
  };
  const onAddMoreActivityClick = () => openExtra();
  const onExtraFormChange = (event) => {
    if (event.target.name === "kind") { customDaySelection = false; populateCatalog(event.target.value); updateCatalogFields(); }
    if (event.target.name === "catalogId") { customDaySelection = false; updateCatalogFields(); }
    if (event.target.name === "weekday") {
      const code = extraForm.elements.catalogId.value;
      const variant = code && code !== "manual" ? catalogVariantForWeekday(extraForm.elements.kind.value, code, event.target.value) : null;
      if (variant) {
        extraForm.elements.title.value = variant.title;
        extraForm.elements.professor.value = variant.professor || "";
        extraForm.elements.start.value = variant.start;
        extraForm.elements.end.value = variant.end;
      }
      $("#catalogTeacher").textContent = variant?.professor || "";
    }
  };
  const onToggleCustomDayClick = () => { customDaySelection = true; updateCatalogFields(false); };
  const onExtraSubmit = (event) => {
    event.preventDefault();
    const fields = Object.fromEntries(new FormData(extraForm));
    const variants = fields.catalogId && fields.catalogId !== "manual" ? catalogVariants(fields.kind, fields.catalogId) : [];
    const variant = catalogVariantForWeekday(fields.kind, fields.catalogId, fields.weekday);
    const fallback = variant || variants[0] || null;
    const candidate = {
      ...fields, id: editingExtraId || generateId(), catalogId: variant?.id || "",
      title: fallback?.title || fields.title, professor: fallback?.professor || fields.professor,
      location: fallback?.location || ""
    };
    const activity = engine.normalizeExtraActivities([candidate])[0];
    if (!activity) { showError("#extraError", "Confira o nome, os horários e as datas. O término deve ser depois do início."); return; }
    const duplicate = draft.extraActivities.some((item) => item.id !== editingExtraId && item.kind === activity.kind && item.title === activity.title &&
      item.weekday === activity.weekday && item.start === activity.start && item.end === activity.end && item.startDate === activity.startDate && item.endDate === activity.endDate);
    if (duplicate) { showError("#extraError", "Esse horário já está na sua lista."); return; }
    const index = draft.extraActivities.findIndex((item) => item.id === editingExtraId);
    if (index >= 0) draft.extraActivities[index] = activity;
    else draft.extraActivities.push(activity);
    renderExtras();
    extraDialog.close();
  };
  const onRootClick = (event) => {
    const button = event.target.closest("button");
    if (!button) return;
    if (button.dataset.day) selectDay(Number(button.dataset.day));
    if (button.dataset.addKind) openExtra(button.dataset.addKind);
    if (button.dataset.editExtra) openExtra("elective", button.dataset.editExtra);
    if (button.dataset.removeExtra) {
      draft.extraActivities = draft.extraActivities.filter((item) => item.id !== button.dataset.removeExtra);
      renderExtras();
    }
    if (button.dataset.closeDialog) $("#" + button.dataset.closeDialog).close();
  };
  const onWeekdayTabsKeydown = (event) => {
    let day = selectedDay;
    if (event.key === "ArrowRight") day = selectedDay % 5 + 1;
    else if (event.key === "ArrowLeft") day = (selectedDay + 3) % 5 + 1;
    else if (event.key === "Home") day = 1;
    else if (event.key === "End") day = 5;
    else return;
    event.preventDefault();
    selectDay(day, true);
  };
  const onWideLayoutChange = () => renderSchedule();
  function refreshForTime() {
    const currentDate = dateToISO(now());
    if (currentDate !== lastDate) { lastDate = currentDate; selectedDay = Math.min(5, Math.max(1, now().getDay())); }
    if (!settingsDialog.open) renderSchedule();
  }
  const onVisibilityChange = () => { if (!document.hidden) refreshForTime(); };
  const onStorage = (event) => {
    if (event.key === STORAGE_KEY && !settingsDialog.open) { profile = loadProfile(); blockOverride = null; weekOverride = null; renderSchedule(); }
  };
  const onPreviousWeekClick = () => moveWeek(-1);
  const onNextWeekClick = () => moveWeek(1);

  $("#profileButton").addEventListener("click", onProfileButtonClick);
  $("#closeSettings").addEventListener("click", onCloseSettingsClick);
  $("#blockButton").addEventListener("click", onBlockButtonClick);
  $("#previousWeek").addEventListener("click", onPreviousWeekClick);
  $("#nextWeek").addEventListener("click", onNextWeekClick);
  $("#closeBlockDialog").addEventListener("click", onCloseBlockDialogClick);
  $("#blockChoices").addEventListener("change", onBlockChoicesChange);
  $("#resetBlockView").addEventListener("click", onResetBlockViewClick);
  settingsDialog.addEventListener("cancel", onSettingsCancel);
  settingsForm.addEventListener("change", onSettingsChange);
  settingsForm.addEventListener("submit", onSettingsSubmit);
  $("#addMoreActivity").addEventListener("click", onAddMoreActivityClick);
  extraForm.addEventListener("change", onExtraFormChange);
  $("#toggleCustomDay").addEventListener("click", onToggleCustomDayClick);
  extraForm.addEventListener("submit", onExtraSubmit);
  root.addEventListener("click", onRootClick);
  $("#weekdayTabs").addEventListener("keydown", onWeekdayTabsKeydown);
  wideLayout.addEventListener("change", onWideLayoutChange);
  landscapeMedia.addEventListener("change", onWideLayoutChange);
  const intervalId = setInterval(refreshForTime, 60_000);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("storage", onStorage);

  renderSchedule();
  if (!profile.configured) requestAnimationFrame(() => openSettings(true));

  return function unmountHorario() {
    clearInterval(intervalId);
    clearTimeout(toastTimer);
    $("#profileButton")?.removeEventListener("click", onProfileButtonClick);
    $("#closeSettings")?.removeEventListener("click", onCloseSettingsClick);
    $("#blockButton")?.removeEventListener("click", onBlockButtonClick);
    $("#previousWeek")?.removeEventListener("click", onPreviousWeekClick);
    $("#nextWeek")?.removeEventListener("click", onNextWeekClick);
    $("#closeBlockDialog")?.removeEventListener("click", onCloseBlockDialogClick);
    $("#blockChoices")?.removeEventListener("change", onBlockChoicesChange);
    $("#resetBlockView")?.removeEventListener("click", onResetBlockViewClick);
    settingsDialog?.removeEventListener("cancel", onSettingsCancel);
    settingsForm?.removeEventListener("change", onSettingsChange);
    settingsForm?.removeEventListener("submit", onSettingsSubmit);
    $("#addMoreActivity")?.removeEventListener("click", onAddMoreActivityClick);
    extraForm?.removeEventListener("change", onExtraFormChange);
    $("#toggleCustomDay")?.removeEventListener("click", onToggleCustomDayClick);
    extraForm?.removeEventListener("submit", onExtraSubmit);
    root.removeEventListener("click", onRootClick);
    $("#weekdayTabs")?.removeEventListener("keydown", onWeekdayTabsKeydown);
    wideLayout.removeEventListener("change", onWideLayoutChange);
    landscapeMedia.removeEventListener("change", onWideLayoutChange);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("storage", onStorage);
    if (settingsDialog?.open) settingsDialog.close();
    if (extraDialog?.open) extraDialog.close();
    if (blockDialog?.open) blockDialog.close();
  };
}
